// Uzaqdan yeniləmənin vəziyyəti (Electron main → renderer).
//
// Mənbə push-dur, poll deyil: yükləmə tərəqqisi saniyədə bir neçə dəfə dəyişir
// və onu sorğu ilə qovmaq həm gecikdirər, həm mənasız işdir. `state()` yalnız
// bir dəfə — komponent qoşulanda — cari vəziyyəti almaq üçündür.

import { useCallback, useEffect, useState } from 'react';

export type UpdatePhase = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'error';

export interface ReleaseNote {
  version: string;
  date?: string;
  mandatory?: boolean;
  /** dil kodu → mətn */
  notes: Record<string, string>;
}

export interface UpdateState {
  currentVersion: string;
  phase: UpdatePhase;
  newVersion?: string;
  /** 0..100 */
  percent?: number;
  bytesPerSecond?: number;
  /** Bu yeniləmə gözləyə bilməz — geri sayım işləyir. */
  mandatory: boolean;
  restartInSec?: number;
  error?: string;
  /** Son UĞURLU yoxlama (ms). "Güncəldir" iddiası buna söykənir. */
  lastCheckedAt?: number;
  /** Dəyişiklik qeydləri, yeni → köhnə. */
  notes?: ReleaseNote[];
}

declare global {
  interface Window {
    rezmoUpdate?: {
      state(): Promise<UpdateState>;
      check(): Promise<UpdateState>;
      install(): Promise<{ ok: boolean }>;
      onState(cb: (s: UpdateState) => void): () => void;
    };
  }
}

export function useAppUpdate(): {
  update: UpdateState | null;
  install: () => void;
  check: () => void;
} {
  const [update, setUpdate] = useState<UpdateState | null>(null);

  useEffect(() => {
    const api = window.rezmoUpdate;
    if (!api) return undefined; // brauzer / dev — yeniləmə anlayışı yoxdur

    let alive = true;
    void api.state().then((s) => {
      if (alive) setUpdate(s);
    });

    const off = api.onState((s) => {
      if (alive) setUpdate(s);
    });

    return () => {
      alive = false;
      off();
    };
  }, []);

  const install = useCallback(() => {
    void window.rezmoUpdate?.install();
  }, []);

  const check = useCallback(() => {
    void window.rezmoUpdate?.check();
  }, []);

  return { update, install, check };
}
